import { useState, useCallback, useMemo } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { PermissionStatus } from 'expo-location'
import { selectPatientProfileLocationCoordinates } from 'APP/Redux/PatientRedux'
import { requestLocation } from 'APP/NativeModules/PermissionsService'
import { getCurrentPosition } from 'APP/Sagas/PatientSagas'
import PatientActions from 'APP/Redux/PatientRedux'
import i18n from 'APP/I18n'

export type Coordinates = {
  latitude: number
  longitude: number
}

export type LocationState = {
  hasLocation: boolean
  coordinates: Coordinates | null
  error: string | undefined
  hasNoLocationPermission: boolean
  requestLocationPermission: (signal?: AbortSignal) => Promise<void>
}

export const NO_LOCATION_PERMISSION_ERROR = i18n.t('SelfNavigationScreen.searchLocation.required')

export const useLocation = ({
  requestOnFocus = true,
}: { requestOnFocus?: boolean } = {}): LocationState => {
  const coordinates = useSelector(selectPatientProfileLocationCoordinates)
  const dispatch = useDispatch()
  const [error, setError] = useState<string>()
  const hasLocation = useMemo(() => !!coordinates, [coordinates])
  const [hasNoLocationPermission, setHasNoLocationPermission] = useState(false)

  const reset = useCallback(() => {
    setError(undefined)
    setHasNoLocationPermission(false)
  }, [])

  const getAndStoreLocation = useCallback(async () => {
    try {
      const location = await getCurrentPosition()
      dispatch(
        PatientActions.patientUpdateLocationSuccess({
          longitude: location.longitude,
          latitude: location.latitude,
        })
      )
      reset()
    } catch (_error) {
      setError(NO_LOCATION_PERMISSION_ERROR)
      setHasNoLocationPermission(true)
    }
  }, [dispatch, reset])

  const requestLocationPermission = useCallback(
    async (signal?: AbortSignal) => {
      reset()

      if (signal?.aborted) return

      try {
        const permission = await requestLocation()

        if (signal?.aborted) return

        if (permission === PermissionStatus.GRANTED) {
          await getAndStoreLocation()
          return
        }

        setError(NO_LOCATION_PERMISSION_ERROR)
        setHasNoLocationPermission(true)
      } catch (_error) {
        if (!signal?.aborted) {
          setError(NO_LOCATION_PERMISSION_ERROR)
        }
      }
    },
    [getAndStoreLocation, reset]
  )

  useFocusEffect(
    useCallback(() => {
      if (!requestOnFocus) return

      const abortController = new AbortController()

      if (!hasLocation) {
        requestLocationPermission(abortController.signal)
      } else {
        reset()
      }

      return () => abortController.abort()
    }, [hasLocation, requestLocationPermission, reset, requestOnFocus])
  )

  return {
    hasLocation,
    coordinates,
    error,
    hasNoLocationPermission,
    requestLocationPermission,
  }
}
